import { SporsmalType } from '../../types/generated-types';
import { BesvartSporsmal, CurrentSporsmal, isCurrentSporsmalValid, toBesvartSporsmal } from '../reducer/current-steg';
import { UpdateSporsmalListAction } from '../reducer/sporsmalsflyt-actions';
import { SporsmalList } from '../reducer/sporsmalsflyt-types';
import {
    getSporsmalVisibilityStatesFromCollection,
    getVisibleSporsmalFromCollection,
    initializeSporsmalsflytCollection,
    SporsmalsflytCollection,
    updateSporsmalsflytCollection,
} from '../sporsmalsflyt-collection';

export interface CurrentSporsmalList {
    type: SporsmalType.SporsmalList;
    sporsmal: SporsmalList;
    sporsmalListSporsmalsflytCollection: SporsmalListSporsmalsflytCollection[];
}

export interface SporsmalListSporsmalsflytCollection {
    id: string;
    expanded: boolean;
    sporsmalsflytCollection: SporsmalsflytCollection;
}

export interface BesvartSporsmalList extends CurrentSporsmalList {
    besvarteSporsmal: BesvartSporsmal[][];
}

export const initializeCurrentSporsmalList = (
    sporsmalList: SporsmalList,
    besvarteSporsmal: BesvartSporsmal[],
): CurrentSporsmalList => {
    return {
        type: SporsmalType.SporsmalList,
        sporsmal: sporsmalList,
        sporsmalListSporsmalsflytCollection: [
            {
                id: '0',
                expanded: true,
                sporsmalsflytCollection: initializeSporsmalsflytCollection(
                    sporsmalList.sporsmalsflyt,
                    besvarteSporsmal,
                ),
            },
        ],
    };
};

export const isCurrentSporsmalList = (currentSporsmal: CurrentSporsmal): currentSporsmal is CurrentSporsmalList =>
    currentSporsmal.type === SporsmalType.SporsmalList;

export const getSporsmalListVisibilityStates = (
    sporsmalsflytCollection: SporsmalsflytCollection,
): { sporsmal: CurrentSporsmal; show: boolean }[] => getSporsmalVisibilityStatesFromCollection(sporsmalsflytCollection);

export const updateCurrentSporsmalList = (
    currentSporsmal: CurrentSporsmal,
    action: UpdateSporsmalListAction,
    besvarteSporsmalFromOutsideCollection: BesvartSporsmal[],
): CurrentSporsmal => {
    if (isCurrentSporsmalList(currentSporsmal)) {
        return {
            ...currentSporsmal,
            sporsmalListSporsmalsflytCollection: currentSporsmal.sporsmalListSporsmalsflytCollection.map(
                (
                    currentSporsmalflytCollection: SporsmalListSporsmalsflytCollection,
                ): SporsmalListSporsmalsflytCollection => {
                    if (currentSporsmalflytCollection.id === action.sporsmalCollectionId) {
                        return {
                            ...currentSporsmalflytCollection,
                            sporsmalsflytCollection: updateSporsmalsflytCollection(
                                currentSporsmalflytCollection.sporsmalsflytCollection,
                                currentSporsmal.sporsmal.sporsmalsflyt,
                                action.action,
                                besvarteSporsmalFromOutsideCollection,
                            ),
                        };
                    }
                    return currentSporsmalflytCollection;
                },
            ),
        };
    }
    return currentSporsmal;
};

export const isVisible = (sporsmalId: string, sporsmalsflytCollection: SporsmalsflytCollection) => {
    return getSporsmalListVisibilityStates(sporsmalsflytCollection).find(
        (currentSporsmal) => currentSporsmal.sporsmal.sporsmal.id === sporsmalId && currentSporsmal.show,
    );
};

export const sporsmalListValid = (sporsmalListSporsmalsflytCollections: SporsmalListSporsmalsflytCollection[]) => {
    return sporsmalListSporsmalsflytCollections.every(
        (sporsmalListSporsmalsflytCollection: SporsmalListSporsmalsflytCollection) =>
            sporsmalListSporsmalsflytCollection.sporsmalsflytCollection.currentSporsmalList
                .filter((x) => isVisible(x.sporsmal.id, sporsmalListSporsmalsflytCollection.sporsmalsflytCollection))
                .every((currentSporsmal: CurrentSporsmal) => isCurrentSporsmalValid(currentSporsmal)),
    );
};

export const addSporsmalListSporsToCurrent = (
    currentSporsmal: CurrentSporsmal,
    besvarteSporsmal: BesvartSporsmal[],
): CurrentSporsmal => {
    if (isCurrentSporsmalList(currentSporsmal)) {
        const max = Math.max(...currentSporsmal.sporsmalListSporsmalsflytCollection.map((c) => parseInt(c.id, 10))) + 1;
        return {
            ...currentSporsmal,
            sporsmalListSporsmalsflytCollection: [
                ...currentSporsmal.sporsmalListSporsmalsflytCollection.map((s) => {
                    return {
                        ...s,
                        expanded: false,
                    };
                }),
                {
                    id: `${max}`,
                    expanded: true,
                    sporsmalsflytCollection: initializeSporsmalsflytCollection(
                        currentSporsmal.sporsmal.sporsmalsflyt,
                        besvarteSporsmal,
                    ),
                },
            ],
        };
    }
    return currentSporsmal;
};

export const removeSporsmalListSporsToCurrent = (currentSporsmal: CurrentSporsmal, id: string): CurrentSporsmal => {
    if (isCurrentSporsmalList(currentSporsmal)) {
        return {
            ...currentSporsmal,
            sporsmalListSporsmalsflytCollection: currentSporsmal.sporsmalListSporsmalsflytCollection.filter(
                (s) => s.id !== id,
            ),
        };
    }
    return currentSporsmal;
};

export const toggleSporsmalsListExapndCard = (currentSporsmal: CurrentSporsmal, id: string): CurrentSporsmal => {
    if (isCurrentSporsmalList(currentSporsmal)) {
        return {
            ...currentSporsmal,
            sporsmalListSporsmalsflytCollection: currentSporsmal.sporsmalListSporsmalsflytCollection.map((s) => {
                return {
                    ...s,
                    expanded: s.id === id ? !s.expanded : false,
                };
            }),
        };
    }
    return currentSporsmal;
};

export const copyNextSvarToCurrentSporsmalList = (
    currentSporsmal: CurrentSporsmalList,
    nextSporsmal: CurrentSporsmalList,
): CurrentSporsmalList => ({
    ...currentSporsmal,
    sporsmalListSporsmalsflytCollection: nextSporsmal.sporsmalListSporsmalsflytCollection,
});

export const toBesvartSporsmalList = (currentSporsmal: CurrentSporsmal): BesvartSporsmalList | undefined => {
    if (isCurrentSporsmalList(currentSporsmal)) {
        const sporsmal = currentSporsmal.sporsmalListSporsmalsflytCollection
            .map((sporsmalsflytCollection) =>
                getVisibleSporsmalFromCollection(sporsmalsflytCollection.sporsmalsflytCollection),
            )
            .map((currentSporsmal) => currentSporsmal.map(toBesvartSporsmal));

        if (!sporsmal.some((s) => s === undefined)) {
            return {
                ...currentSporsmal,
                besvarteSporsmal: sporsmal,
            };
        }
    }
    return undefined;
};
